import { createServerFn } from "@tanstack/react-start";
import { normalizePhoneOrThrow } from "@/lib/phone";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { SHIRT_SIZE_OPTIONS } from "@/lib/claim-fields";
import { assertApprovedVolunteer } from "@/lib/volunteer-guard.server";
import {
  SIGNATURE_LOGO_URL,
  appendSignature,
  cleanSignature,
} from "@/lib/email-signature";
import { reportHandledSync } from "./observability";

export const getMyContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const [rolesRes, profileRes, assignmentsRes] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", userId),
      supabase.from("volunteer_profiles").select("*").eq("user_id", userId).maybeSingle(),
      supabase
        .from("volunteer_assignments")
        .select("id, role_on_program, starts_on, ends_on, programs(id, name, slug, site_url)")
        .eq("volunteer_id", userId),
    ]);

    if (rolesRes.error) throw new Error(rolesRes.error.message);

    const roles = (rolesRes.data ?? []).map((r) => r.role as string);
    const email = (context.claims as { email?: string } | null)?.email ?? "";
    let profile = profileRes.data ?? null;

    // Every signed-in account gets a profile row so the outreach address is
    // assigned right away instead of waiting for a first manual save.
    if (!profile) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const metaName =
        (context.claims as { user_metadata?: { full_name?: string; name?: string } } | null)
          ?.user_metadata?.full_name ??
        (context.claims as { user_metadata?: { name?: string } } | null)?.user_metadata?.name ??
        "";
      const created = await supabaseAdmin
        .from("volunteer_profiles")
        .insert({
          user_id: userId,
          email,
          full_name: metaName || email.split("@")[0] || "Volunteer",
          status: roles.includes("admin") ? "active" : "pending",
        })
        .select("*")
        .maybeSingle();
      profile = created.data ?? null;
    }

    return {
      userId,
      email,
      roles,
      isAdmin: roles.includes("admin"),
      isVolunteer: roles.includes("volunteer"),
      profile,
      assignments: assignmentsRes.data ?? [],
    };
  });


export const claimFirstAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: existing, error: checkError } = await supabaseAdmin
      .from("user_roles")
      .select("id")
      .eq("role", "admin")
      .limit(1);
    if (checkError) throw new Error(checkError.message);
    if ((existing ?? []).length > 0) return { claimed: false };

    const { error } = await supabaseAdmin
      .from("user_roles")
      .upsert({ user_id: context.userId, role: "admin" }, { onConflict: "user_id,role" });
    if (error) throw new Error(error.message);
    return { claimed: true };
  });

export const saveMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      full_name: string;
      phone?: string;
      birthday?: string;
      
      
      city?: string;
      state?: string;
      t1d_connection?: string;
      shirt_size?: string;
      volunteer_track?: string;
      availability?: string;
      why_help?: string;
      programs_of_interest?: string[];
      skills?: string[];
      email_signature?: string;
      signature_title?: string;
      signature_include_logo?: boolean;
    }) => data,
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const email = (context.claims as { email?: string } | null)?.email ?? "";
    const track = ["remote", "onsite", "both"].includes(data.volunteer_track ?? "")
      ? (data.volunteer_track as string)
      : "remote";
    const payload = {
      email,
      full_name: (data.full_name ?? "").trim().slice(0, 120),
      phone: normalizePhoneOrThrow(data.phone),
      birthday: /^\d{4}-\d{2}-\d{2}$/.test((data.birthday ?? "").trim())
        ? (data.birthday as string).trim()
        : null,
      region: null,
      city: data.city?.trim().slice(0, 120) || null,
      state: data.state?.trim().slice(0, 60) || null,
      t1d_connection: data.t1d_connection?.trim().slice(0, 1000) || null,
      shirt_size: SHIRT_SIZE_OPTIONS.includes((data.shirt_size ?? "").trim())
        ? (data.shirt_size as string).trim()
        : null,
      volunteer_track: track,
      availability: data.availability?.trim().slice(0, 500) || null,
      why_help: data.why_help?.trim().slice(0, 2000) || null,
      programs_of_interest: (data.programs_of_interest ?? []).slice(0, 12),
      skills: (data.skills ?? []).slice(0, 20),
      email_signature: cleanSignature(data.email_signature) || null,
      signature_title: data.signature_title?.trim().slice(0, 120) || null,
      signature_include_logo: Boolean(data.signature_include_logo),
    };

    // Profiles are only created by an approved application or by an admin.
    // Saving here must never provision a brand new roster entry.
    const existing = await supabase
      .from("volunteer_profiles")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();

    if (!existing.data) {
      throw new Error(
        "You do not have a volunteer profile yet. Submit the volunteer application and we will set one up once it is approved.",
      );
    }

    const { error } = await supabase
      .from("volunteer_profiles")
      .update(payload)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };

  });

export const saveMySignature = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: { email_signature: string; signature_include_logo?: boolean }) => data,
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("volunteer_profiles")
      .update({
        email_signature: cleanSignature(data.email_signature) || null,
        ...(data.signature_include_logo === undefined
          ? {}
          : { signature_include_logo: Boolean(data.signature_include_logo) }),
      })
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listKits = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("enablement_kits")
      .select("*, programs(id, name, slug, site_url, sort_order)")
      .eq("is_published", true);
    if (error) throw new Error(error.message);
    return (data ?? []).sort(
      (a, b) => (a.programs?.sort_order ?? 0) - (b.programs?.sort_order ?? 0),
    );
  });

export const listMyContacts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    // Owned, shared with me, and team-wide public contacts are all valid send
    // targets, so the compose pickers must see all three.
    const { data, error } = await context.supabase
      .from("outreach_contacts")
      .select("*, programs(id, name)")
      .order("created_at", { ascending: false })
      .limit(2000);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const saveContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      id?: string;
      first_name?: string;
      last_name?: string;
      organization?: string;
      organizations?: Array<{ organization_id: string; role?: string | null }>;
      email?: string;
      phone?: string;
      program_id?: string | null;
      status?: string;
      notes?: string;
      is_public?: boolean;
      share_with?: string[];
    }) => data,
  )
  .handler(async ({ data, context }) => {
    await assertApprovedVolunteer(context);
    const { joinContactName } = await import("@/lib/contact-name");
    const { syncContactOrganizations, applyContactShares } = await import(
      "@/lib/contact-orgs.server"
    );
    const email = data.email?.trim().toLowerCase().slice(0, 255) || "";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error("A valid email address is required.");
    }
    const firstName = data.first_name?.trim().slice(0, 80) || null;
    const lastName = data.last_name?.trim().slice(0, 80) || null;
    const payload = {
      volunteer_id: context.userId,
      first_name: firstName,
      last_name: lastName,
      contact_name: joinContactName(firstName, lastName),
      organization: data.organization?.trim().slice(0, 160) || null,
      email,
      phone: normalizePhoneOrThrow(data.phone),
      program_id: data.program_id || null,
      notes: data.notes?.trim().slice(0, 2000) || null,
      is_public: Boolean(data.is_public),
    };

    if (data.id) {
      // Edit forms do not carry the pipeline status, so only write it when the
      // caller explicitly sends one. Defaulting here would reset every edited
      // contact back to "new".
      const { volunteer_id: _owner, ...editable } = payload;
      const updates = data.status ? { ...editable, status: data.status } : editable;
      // RLS allows the owner, an admin, or (for public contacts) any approved
      // volunteer to update the row.
      const { error } = await context.supabase
        .from("outreach_contacts")
        .update(updates)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      await syncContactOrganizations(data.id, data.organizations);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const owner = await supabaseAdmin
        .from("outreach_contacts")
        .select("volunteer_id")
        .eq("id", data.id)
        .maybeSingle();
      await applyContactShares(
        data.id,
        owner.data?.volunteer_id ?? context.userId,
        data.share_with,
        context.userId,
      );
      return { id: data.id, shared: false, owner_name: null };
    }

    // One contact per email address across the whole team. A second attempt
    // shares the original instead of creating a duplicate history.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const existing = await supabaseAdmin
      .from("outreach_contacts")
      .select("id, volunteer_id")
      .eq("email", email)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (existing.error) throw new Error(existing.error.message);

    if (existing.data) {
      await syncContactOrganizations(existing.data.id, data.organizations);
      await applyContactShares(
        existing.data.id,
        existing.data.volunteer_id,
        data.share_with,
        context.userId,
      );
      if (existing.data.volunteer_id === context.userId) {
        return { id: existing.data.id, shared: false, owner_name: null };
      }
      const { error: shareError } = await supabaseAdmin
        .from("outreach_contact_shares")
        .upsert(
          {
            contact_id: existing.data.id,
            volunteer_id: context.userId,
            shared_by: existing.data.volunteer_id,
          },
          { onConflict: "contact_id,volunteer_id" },
        );
      if (shareError) throw new Error(shareError.message);

      const owner = await supabaseAdmin
        .from("volunteer_profiles")
        .select("full_name, email")
        .eq("user_id", existing.data.volunteer_id)
        .maybeSingle();
      return {
        id: existing.data.id,
        shared: true,
        owner_name: owner.data?.full_name || owner.data?.email || "another volunteer",
      };
    }

    const { data: inserted, error } = await context.supabase
      .from("outreach_contacts")
      .insert({ ...payload, status: data.status ?? "new" })
      .select("id")
      .single();

    if (error) throw new Error(error.message);
    await syncContactOrganizations(inserted.id, data.organizations);
    await applyContactShares(inserted.id, context.userId, data.share_with, context.userId);
    return { id: inserted.id, shared: false, owner_name: null };
  });


/** Removes a shared contact from a collaborator's list. Never deletes history. */
export const unshareContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { contact_id: string; volunteer_id?: string }) => data)
  .handler(async ({ data, context }) => {
    const target = data.volunteer_id || context.userId;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    if (target !== context.userId) {
      const owner = await supabaseAdmin
        .from("outreach_contacts")
        .select("volunteer_id")
        .eq("id", data.contact_id)
        .maybeSingle();
      if (owner.error) throw new Error(owner.error.message);
      if (owner.data?.volunteer_id !== context.userId) {
        throw new Error("Only the contact owner can remove other collaborators.");
      }
    }

    const { error } = await supabaseAdmin
      .from("outreach_contact_shares")
      .delete()
      .eq("contact_id", data.contact_id)
      .eq("volunteer_id", target);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const deleteContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("outreach_contacts")
      .delete()
      .eq("id", data.id)
      .eq("volunteer_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const sendKitEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { contact_id: string; kit_id: string; subject: string; body: string }) =>
    data,
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertApprovedVolunteer(context);



    // RLS lets the owner, an admin, or a shared collaborator read the contact.
    const contact = await supabase
      .from("outreach_contacts")
      .select("id, contact_name, email, program_id, status")
      .eq("id", data.contact_id)
      .maybeSingle();
    if (contact.error) throw new Error(contact.error.message);
    if (!contact.data) throw new Error("Contact not found.");
    if (!contact.data.email) throw new Error("This contact has no email address.");

    const subject = (data.subject ?? "").trim().slice(0, 200);
    const body = (data.body ?? "").trim().slice(0, 20000);
    if (!subject || !body) throw new Error("Subject and message are both required.");

    const volunteerEmail = (context.claims as { email?: string } | null)?.email ?? "";
    const profile = await supabase
      .from("volunteer_profiles")
      .select("full_name, alias_local_part, email_signature, signature_include_logo")
      .eq("user_id", userId)
      .maybeSingle();

    const { ensureVolunteerAlias } = await import("@/lib/volunteer-alias.server");
    const { aliasAddress, formatSender } = await import("@/lib/volunteer-alias");
    const aliasLocal =
      profile.data?.alias_local_part ??
      (await ensureVolunteerAlias(userId, profile.data?.full_name ?? ""));
    const senderAddress = aliasLocal ? aliasAddress(aliasLocal) : null;

    const signedBody = appendSignature(body, profile.data?.email_signature);

    const { data: inserted, error } = await supabase
      .from("outreach_sends")
      .insert({
        volunteer_id: userId,
        contact_id: contact.data.id,
        kit_id: data.kit_id,
        program_id: contact.data.program_id,
        subject,
        body: signedBody,
        delivery_status: "pending",
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    let deliveryStatus = "sent";
    let deliveryNote: string | null = null;

    try {
      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      const result = await sendTemplateEmail("kit-outreach", contact.data.email, {
        templateData: {
          subjectLine: subject,
          body: signedBody,
          volunteerName: profile.data?.full_name ?? "",
          volunteerEmail: senderAddress ?? volunteerEmail,
          signatureLogoUrl:
            profile.data?.signature_include_logo && profile.data?.email_signature
              ? SIGNATURE_LOGO_URL
              : undefined,
        },
        idempotencyKey: `kit-outreach-${inserted.id}`,
        from: senderAddress
          ? formatSender(profile.data?.full_name ?? "", senderAddress)
          : undefined,
        replyTo: senderAddress ?? volunteerEmail ?? undefined,
        headers: { "X-Warrior-Send-Id": inserted.id },
      });

      if (!result.sent) {
        deliveryStatus = "suppressed";
        deliveryNote = "Recipient is suppressed (previous bounce, complaint, or unsubscribe).";
      }
    } catch (err) {
      reportHandledSync(err, { feature: "volunteer", action: "volunteerEmail" });
      deliveryStatus = "failed";
      deliveryNote = err instanceof Error ? err.message : String(err);
    }

    await supabase
      .from("outreach_sends")
      .update({ delivery_status: deliveryStatus })
      .eq("id", inserted.id)
      .eq("volunteer_id", userId);

    if (deliveryStatus === "sent" && contact.data.status === "new") {
      await supabase
        .from("outreach_contacts")
        .update({ status: "contacted" })
        .eq("id", contact.data.id)
        .eq("volunteer_id", userId);
    }

    if (deliveryStatus === "failed") {
      throw new Error(deliveryNote ?? "The email could not be sent.");
    }

    return { ok: true, delivery_status: deliveryStatus, note: deliveryNote };
  });


export const listMySends = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("outreach_sends")
      .select("*, outreach_contacts(contact_name, organization), programs(name)")
      .eq("volunteer_id", context.userId)
      .order("sent_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const listMyActivity = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("activity_log")
      .select("*, programs(name)")
      .eq("volunteer_id", context.userId)
      .order("activity_date", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const logActivity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      program_id?: string | null;
      organization_id?: string | null;
      activity_type: string;
      hours: number;
      activity_date: string;
      notes?: string;
    }) => data,
  )
  .handler(async ({ data, context }) => {
    const { activityCompanyId } = await import("@/lib/activity-company");
    const hours = Number(data.hours);
    if (!Number.isFinite(hours) || hours < 0 || hours > 999) throw new Error("Enter valid hours.");
    const { error } = await context.supabase.from("activity_log").insert({
      volunteer_id: context.userId,
      program_id: data.program_id || null,
      organization_id: activityCompanyId(data.organization_id),
      activity_type: (data.activity_type ?? "").trim().slice(0, 80) || "General",
      hours,
      activity_date: data.activity_date,
      notes: data.notes?.trim().slice(0, 2000) || null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteActivity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("activity_log")
      .delete()
      .eq("id", data.id)
      .eq("volunteer_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listProgramsAuthed = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("programs")
      .select("id, name, slug, summary, site_url, sort_order, is_active")
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/**
 * Sign-in gate: a valid login is not the same as an approved volunteer.
 * Returns why an account is blocked so the sign-in page can say the right
 * thing ("still under review" vs "we have no application for you").
 */
export const getSignInGate = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const email = ((context.claims as { email?: string } | null)?.email ?? "")
      .trim()
      .toLowerCase();

    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (isAdmin) return { allowed: true as const, reason: "approved" as const };

    const { data: profile } = await supabase
      .from("volunteer_profiles")
      .select("status")
      .eq("user_id", userId)
      .maybeSingle();
    if ((profile as { status?: string } | null)?.status === "active") {
      return { allowed: true as const, reason: "approved" as const };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: application } = await supabaseAdmin
      .from("volunteer_applications")
      .select("id")
      .ilike("email", email)
      .limit(1);

    return {
      allowed: false as const,
      reason: (application?.length ? "pending" : "no_application") as
        | "pending"
        | "no_application",
    };
  });
